// steps/tabs/meter-readings/DashboardTab.tsx
import {
  useMeterReadingAnalysis,
  useMeterReadingSummary,
} from "@features/bx/BulkBillGeneration/hooks";
import { QuickStatsCard } from "@shared/components/QuickStatsCard";
import { AlertCircle, CheckCircle, FileText, RefreshCcw } from "lucide-react";
import React from "react";
import { useParams } from "react-router-dom";

interface DashboardTabProps {
  data?: any;
}

const DashboardTab: React.FC<DashboardTabProps> = ({ data }) => {
  const { id, cycleId, action } = useParams();

  const { data: dashboardData } = useMeterReadingAnalysis(cycleId);
  const { data: meterReadingSummary } = useMeterReadingSummary(cycleId);

  // Progress cards data
  const progressCards = [
    {
      title: "Reading Collection Status",
      subtitle: "Collection progress comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.readingCollection?.currentPercentage ?? "NA",
        date: "Reading Collection Date: Jan 15, 2024 - Jan 20, 2024",
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.readingCollection?.previousPercentage ?? "NA",
      },
      change: `Increased by ${dashboardData?.readingCollection?.differencePercentage} compared to previous cycle`,
    },
    {
      title: "Reading Validation Status",
      subtitle: "Validation progress comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.readingValidation?.currentPercentage ?? "NA",
        date: "Validation Completion Date: Jan 22, 2024 - Jan 25, 2024",
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.readingValidation?.previousPercentage ?? "NA",
      },
      change: `Increased by ${dashboardData?.readingValidation?.differencePercentage} compared to previous cycle`,
    },
    {
      title: "High Consumption Consumer",
      subtitle: "High consumption progress comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.highConsumption?.currentPercentage ?? "NA",
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.highConsumption?.previousPercentage ?? "NA",
      },
      change: `Increased by ${dashboardData?.highConsumption?.differencePercentage} compared to previous cycle`,
    },
    {
      title: "Low Consumption Consumer",
      subtitle: "Low consumption progress comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.lowConsumption?.currentPercentage ?? "NA",
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.lowConsumption?.previousPercentage ?? "NA",
      },
      change: `Increased by ${dashboardData?.lowConsumption?.differencePercentage} compared to previous cycle`,
    },
    {
      title: "Zero Consumption Consumer",
      subtitle: "Zero consumption progress comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.zeroConsumption?.currentPercentage ?? "NA",
        count: `${dashboardData?.zeroConsumption?.currentConsumerCount} consumers with zero consumption`,
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.zeroConsumption?.previousPercentage ?? "NA",
        count: `${dashboardData?.zeroConsumption?.previousConsumerCount} consumers with zero consumption`,
      },
      change: `Increased by ${dashboardData?.zeroConsumption?.differencePercentage} compared to previous cycle`,
    },
    {
      title: "Unbilled Consumers",
      subtitle: "Unbilled consumers comparison",
      currentCycle: {
        label: "Current Cycle",
        value: dashboardData?.unbilled?.currentPercentage ?? "NA",
        count: `${dashboardData?.unbilled?.currentConsumerCount} unbilled consumers`,
      },
      previousCycle: {
        label: "Previous Cycle",
        value: dashboardData?.unbilled?.previousPercentage ?? "NA",
        count: `${dashboardData?.unbilled?.previousConsumerCount} unbilled consumers`,
      },
      change: `Increased by ${dashboardData?.unbilled?.differencePercentage} compared to previous cycle`,
    },
  ];

  // Reading types distribution
  const readingTypes = [
    {
      label: "Total Readings",
      value: meterReadingSummary?.readingTypesDistribution?.totalReadings,
      icon: <FileText className="w-6 h-6 text-gray-600" />,
    },
    {
      label: "Normal",
      value: meterReadingSummary?.readingTypesDistribution?.normal,
      icon: <CheckCircle className="w-6 h-6 text-green-600" />,
    },
    {
      label: "Faulty",
      value: meterReadingSummary?.readingTypesDistribution?.faulty,
      icon: <AlertCircle className="w-6 h-6 text-red-600" />,
    },
    {
      label: "RCNT",
      value: meterReadingSummary?.readingTypesDistribution?.rcnt,
      icon: <RefreshCcw className="w-6 h-6 text-orange-600" />,
    },
  ];

  return (
    <div className="space-y-6">
      {/* Progress Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {progressCards.map((card, index) => (
          <div
            key={index}
            className="bg-white rounded-lg border border-gray-200 p-5"
          >
            <h3 className="font-semibold text-base mb-1">{card.title}</h3>
            <p className="text-xs text-gray-600 mb-4">{card.subtitle}</p>

            {/* Current Cycle */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {card.currentCycle.label}
                </span>
                <span className="text-lg font-bold">
                  {card.currentCycle.value}
                </span>
              </div>
              {card.currentCycle.date && (
                <p className="text-xs text-gray-500">
                  {card.currentCycle.date}
                </p>
              )}
              {card.currentCycle.count && (
                <p className="text-xs text-gray-700 mt-1">
                  {card.currentCycle.count}
                </p>
              )}
              <div className="w-full h-2 bg-blue-100 rounded-full mt-2">
                <div
                  className="h-2 bg-blue-600 rounded-full"
                  style={{
                    width: card.currentCycle.value,
                  }}
                />
              </div>
            </div>

            {/* Previous Cycle */}
            <div className="mb-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-gray-700">
                  {card.previousCycle.label}
                </span>
                <span className="text-lg font-bold">
                  {card.previousCycle.value}
                </span>
              </div>
              {card.previousCycle.count && (
                <p className="text-xs text-gray-700 mt-1">
                  {card.previousCycle.count}
                </p>
              )}
              <div className="w-full h-2 bg-gray-100 rounded-full mt-2">
                <div
                  className="h-2 bg-gray-400 rounded-full"
                  style={{
                    width: card.previousCycle.value,
                  }}
                />
              </div>
            </div>

            {/* Change Indicator */}
            <p className="text-xs text-gray-600 italic">{card.change}</p>
          </div>
        ))}
      </div>

      {/* Reading Types Distribution */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="font-semibold text-lg mb-1">
          Reading Types Distribution
        </h3>
        <p className="text-sm text-gray-600 mb-4">Normal vs RCNT vs Faulty</p>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {readingTypes.map((type, index) => (
            <QuickStatsCard
              key={index}
              title={type.label}
              count={type.value ?? "NA"}
              icon={type.icon}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
